import { FireControllerAssociationApi, FireControllerApi, SensorsApi, ApiClient } from '../generated/src';
import API_CONFIG from '../config/apiConfig';

/**
 * Service for managing Fire Controller data
 * Handles fetching Fire Controller associations and their information
 */
class FireControllerService {
  constructor() {
    this.apiClient = null;
    this.associationApi = null;
    this.fireControllerApi = null;
    this.sensorsApi = null;
    this.tokenGetter = null;
  }

  /**
   * Initialize the API client with the given base URL
   * @param {string} baseUrl - The Fire Bridge server base URL
   * @param {Function} getTokenFn - Function that returns the current authentication token
   * @throws {Error} If the ApiClient fails to construct with the provided base URL
   */
  initialize(baseUrl, getTokenFn) {
    try {
      this.apiClient = new ApiClient(baseUrl);
      this.apiClient.timeout = API_CONFIG.TIMEOUT;

      if (getTokenFn && typeof getTokenFn === 'function') {
        this.tokenGetter = getTokenFn;
      }

      this.associationApi = new FireControllerAssociationApi(this.apiClient);
      this.fireControllerApi = new FireControllerApi(this.apiClient);
      this.sensorsApi = new SensorsApi(this.apiClient);

      console.log(`FireControllerService initialized with base URL: ${baseUrl}`);
    } catch (error) {
      console.error('Failed to initialize FireControllerService:', error);
      throw error;
    }
  }

  /**
   * Update the authentication token before making API calls
   */
  updateToken() {
    if (this.tokenGetter && this.apiClient) {
      const currentToken = this.tokenGetter();
      if (currentToken) {
        this.apiClient.authentications['BearerAuth'].accessToken = currentToken;
      }
    }
  }

  /**
   * Fetch Fire Controller IDs associated with a user
   * @param {string} userId - The user ID
   * @returns {Promise<Array<string>>} Array of Fire Controller IDs
   */
  async getFireControllerIds(userId) {
    if (!this.associationApi) {
      throw new Error('FireControllerService not initialized. Call initialize() first.');
    }

    this.updateToken();

    return new Promise((resolve, reject) => {
      this.associationApi.fireControllerAssocationUserIdGetFireController(userId, (error, data) => {
        if (error) {
          reject(error);
        } else {
          resolve(data || []);
        }
      });
    });
  }

  /**
   * Fetch Fire Controller device information by ID
   * @param {string} fireControllerId - The Fire Controller ID
   * @returns {Promise<Object>} Fire Controller device information
   */
  async getFireControllerInformation(fireControllerId) {
    if (!this.fireControllerApi) {
      throw new Error('FireControllerService not initialized. Call initialize() first.');
    }

    this.updateToken();

    return new Promise((resolve, reject) => {
      this.fireControllerApi.fireControllerInformationFireControllerIdGet(fireControllerId, (error, data) => {
        if (error) {
          reject(error);
        } else {
          resolve(data);
        }
      });
    });
  }

  /**
   * Fetch all Fire Controller addresses for a user
   * @param {string} userId - The user ID
   * @returns {Promise<Array<Object>>} Array of Fire Controller devices with addresses
   */
  async getFireControllerAddresses(userId) {
    try {
      const fireControllerIds = await this.getFireControllerIds(userId);

      if (!fireControllerIds || fireControllerIds.length === 0) {
        console.log('No Fire Controllers associated with user');
        return [];
      }

      const fireControllerPromises = fireControllerIds.map(id =>
        this.getFireControllerInformation(id)
      );

      const fireControllers = await Promise.all(fireControllerPromises);

      return fireControllers.map(fc => {
        const info = fc.fireControllerInformation;
        const hasCoords = info?.latitude != null && info?.longitude != null;
        return {
          id: fc.fireControllerId,
          address: info?.address,
          latitude: hasCoords ? info.latitude : null,
          longitude: hasCoords ? info.longitude : null,
        };
      });
    } catch (error) {
      console.error('Failed to fetch Fire Controller addresses:', error);
      throw error;
    }
  }

  /**
   * Fetch the hydro system status for a single Fire Controller
   * @param {string} fireControllerId - The Fire Controller ID
   * @returns {Promise<HydroSystemStatus>} The system status object
   */
  async getControllerStatus(fireControllerId) {
    if (!this.sensorsApi) {
      throw new Error('FireControllerService not initialized. Call initialize() first.');
    }

    this.updateToken();

    return new Promise((resolve, reject) => {
      this.sensorsApi.getSpecifiedControllerStatus(fireControllerId, (error, data) => {
        if (error) {
          reject(error);
        } else {
          resolve(data);
        }
      });
    });
  }

  /**
   * Fetch status and location data for all Fire Controllers associated with a user
   * @param {string} userId - The user ID
   * @returns {Promise<Array<FireControllerStatusReport>>} Array of status reports with coordinates
   */
  async getUserFireControllerStatuses(userId) {
    if (!this.fireControllerApi) {
      throw new Error('FireControllerService not initialized. Call initialize() first.');
    }
    if (!userId) {
      return Promise.resolve([]);
    }

    this.updateToken();

    return new Promise((resolve, reject) => {
      this.fireControllerApi.getUserFireControllerStatuses(userId, (error, data) => {
        if (error) {
          reject(error);
        } else {
          resolve(data || []);
        }
      });
    });
  }

  /**
   * Fetch status and location data for all Fire Controllers (admin only)
   * @returns {Promise<Array<FireControllerStatusReport>>} Array of status reports with coordinates
   */
  async getAllFireControllerStatuses() {
    if (!this.fireControllerApi) {
      throw new Error('FireControllerService not initialized. Call initialize() first.');
    }

    this.updateToken();

    return new Promise((resolve, reject) => {
      this.fireControllerApi.getAllFireControllerStatuses((error, data) => {
        if (error) {
          reject(error);
        } else {
          resolve(data || []);
        }
      });
    });
  }

  /**
   * Format address for display (street address only)
   * @param {Object} address - Address object
   * @returns {string} Formatted street address
   */
  formatStreetAddress(address) {
    if (!address) return 'Unknown Address';
    return address.streetAddr || 'Unknown Address';
  }
}

export const fireControllerService = new FireControllerService();
export default fireControllerService;
